/**
 * Main-thread freeze watch.
 *
 * A page that stops answering for seconds is invisible to every report we
 * have: no error is thrown, the boundary never fires, and the browser's own
 * "Page Unresponsive" dialog is the only witness. This watches for those
 * blocks and reports each one as a `client_crash` of category `long_task`
 * with context for investigating the cause:
 *
 *  - how long the thread was blocked (heartbeat drift, or the Long Tasks API
 *    where the browser has it, which is more precise and carries a frame
 *    attribution);
 *  - how the DOM changed during the block: element count before and after,
 *    and a sampled histogram of what was inserted by tag and class (the root
 *    of each inserted subtree, so a runaway list reads as thousands of one
 *    kind of row rather than as its cells);
 *  - the last few same-site requests that completed just before the block,
 *    which are timing clues, not proof of what caused it;
 *  - React commits during the block when the commit hook is present.
 *
 * Nothing here blocks the page: the observers are passive, the histogram is
 * sampled one element in ten, and at most two freezes are reported per tab.
 */

const HEARTBEAT_MS = 1000
/** A heartbeat-only gap at least this long is a suspended page, not a block. */
export const SUSPEND_MS = 30_000

/** A block shorter than this is jank, not a freeze; it is not reported. */
export const FREEZE_MIN_MS = 3000
const MAX_REPORTS_PER_TAB = 2
/** One element in ten is classified; a fixed stride would alias with repeating rows. */
const SAMPLE_RATE = 0.1
const HISTOGRAM_ENTRIES = 6

export interface FreezeInfo {
	/** Length of the block in milliseconds. */
	blockedMs: number
	/** Element count before the block and right after it. */
	nodesBefore: number
	nodesAfter: number
	/** Subtree roots inserted and nodes removed during the block (unsampled totals). */
	addedTotal: number
	removedTotal: number
	/** Sampled histogram of elements added during the block, largest first. */
	added: [string, number][]
	/** Same-site request paths that finished shortly before the block, newest last. */
	recentRequests: string[]
	/** React commits during the block, when the commit hook is installed. */
	commits?: number
	/** JS heap in MB after the block, where the browser exposes it. */
	heapMb?: number
	/** Long Tasks API attribution (`iframe:src` or `window`), when available. */
	attribution?: string
	/** Which mechanism measured the block. */
	source: 'longtask' | 'heartbeat'
}

/** `div.flex.min-w-0` for an element: its tag and up to two classes. */
export const elementKey = (el: Element): string => {
	let className = ''
	try {
		const raw = (el as any).className
		className = typeof raw === 'string' ? raw : typeof raw?.baseVal === 'string' ? raw.baseVal : ''
	} catch {}
	const classes = className.split(/\s+/).filter(Boolean).slice(0, 2)
	return [el.tagName.toLowerCase(), ...classes].join('.').slice(0, 48)
}

const k = (n: number): string => (n >= 1000 ? `${Math.round(n / 1000)}k` : String(n))

/** One line, at most 120 chars, in the crash message alphabet. */
export const freezeMessage = (info: FreezeInfo): string => {
	const parts = [
		`MainThreadFreeze: ${(info.blockedMs / 1000).toFixed(1)}s blocked`,
		`nodes ${k(info.nodesBefore)} to ${k(info.nodesAfter)}`,
		`added ${k(info.addedTotal)} removed ${k(info.removedTotal)}`
	]
	if (info.commits !== undefined) parts.push(`commits ${info.commits}`)
	if (info.heapMb !== undefined) parts.push(`heap ${info.heapMb}MB`)
	return parts
		.join('; ')
		.replace(/[^A-Za-z0-9 _.,:;'()[\]/<>=+-]/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()
		.slice(0, 120)
}

/**
 * The added-element histogram as `div.flex.min-w-0:3200 > img.size-4:900`,
 * in the crash components alphabet and at most 300 chars. Empty when nothing
 * was added, so the field is omitted rather than sent blank.
 */
export const freezeComponents = (info: FreezeInfo): string | undefined => {
	const parts = info.added
		.slice(0, HISTOGRAM_ENTRIES)
		.map(([key, count]) => `${key.replace(/[^A-Za-z0-9_.$\[\]/:@-]/g, '')}:${count}`)
	const joined = parts.join(' > ').slice(0, 300)
	return /^[A-Za-z0-9_.$\[\]/:@> -]{1,300}$/.test(joined) ? joined : undefined
}

/**
 * The requests that finished just before the block and the measurement
 * source, as the error detail: `src=longtask attr=window last=/inscriptions/activity,/mempool/sales`.
 */
export const freezeDetail = (info: FreezeInfo): string | undefined => {
	const parts = [`src=${info.source}`]
	if (info.attribution) parts.push(`attr=${info.attribution}`)
	if (info.recentRequests.length) parts.push(`last=${info.recentRequests.join(',')}`)
	const joined = parts
		.join(' ')
		.replace(/[^A-Za-z0-9_.,:;=()\[\]/ '"·—-]/g, '')
		.slice(0, 200)
	return /^[A-Za-z0-9_.,:;=()\[\]/ '"·—-]{1,200}$/.test(joined) ? joined : undefined
}

/** A request URL reduced to its path with id-like segments collapsed, or nothing for other sites. */
export const requestPath = (name: string): string | undefined => {
	try {
		const url = new URL(name, window.location.href)
		if (!/(^|\.)ordinalswallet\.com$/.test(url.hostname) && url.hostname !== window.location.hostname) {
			return undefined
		}
		if (/\/_next\/|\.(js|css|woff2?|png|jpe?g|webp|svg|gif|ico)$/i.test(url.pathname)) return undefined
		const path = url.pathname
			.split('/')
			.map((segment) => (segment.length > 24 || /^[0-9a-f]{16,}/i.test(segment) ? ':id' : segment))
			.join('/')
			.replace(/[^A-Za-z0-9_./:-]/g, '')
			.slice(0, 40)
		return path || undefined
	} catch {
		return undefined
	}
}

let installed = false

export function installFreezeWatch(report: (info: FreezeInfo) => void): void {
	if (installed || typeof window === 'undefined' || typeof document === 'undefined') return
	installed = true
	try {
		let reports = 0
		let nodesBefore = document.getElementsByTagName('*').length
		let lastBeat = performance.now()
		let visibleSince = performance.now()
		let addedTotal = 0
		let removedTotal = 0
		let added: Record<string, number> = {}
		let commitsAtBeat = 0
		let longest: { duration: number; attribution?: string } | null = null
		const recent: { at: number; path: string }[] = []

		const hook = (window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__
		let commits = 0
		if (hook && typeof hook.onCommitFiberRoot === 'function') {
			const previous = hook.onCommitFiberRoot
			hook.onCommitFiberRoot = function (this: unknown, ...args: unknown[]) {
				commits += 1
				return previous.apply(this, args)
			}
		}
		const commitHookInstalled = Boolean(hook && typeof hook.onCommitFiberRoot === 'function')

		const mutations = new MutationObserver((records) => {
			for (const record of records) {
				removedTotal += record.removedNodes.length
				for (const node of Array.from(record.addedNodes)) {
					if (node.nodeType !== 1) continue
					addedTotal += 1
					if (Math.random() < SAMPLE_RATE) {
						const key = elementKey(node as Element)
						added[key] = (added[key] || 0) + Math.round(1 / SAMPLE_RATE)
					}
				}
			}
		})
		mutations.observe(document.documentElement, { childList: true, subtree: true })

		if (typeof PerformanceObserver === 'function') {
			try {
				const resources = new PerformanceObserver((list) => {
					for (const entry of list.getEntries()) {
						const path = requestPath(entry.name)
						if (!path) continue
						recent.push({ at: entry.startTime + entry.duration, path })
						if (recent.length > 12) recent.splice(0, recent.length - 12)
					}
				})
				resources.observe({ type: 'resource', buffered: true })
			} catch {}
			try {
				const tasks = new PerformanceObserver((list) => {
					for (const entry of list.getEntries()) {
						if (!longest || entry.duration > longest.duration) {
							const attribution = (entry as any).attribution?.[0]
							longest = {
								duration: entry.duration,
								attribution: attribution
									? `${attribution.containerType || 'window'}${attribution.containerSrc ? ':' + String(attribution.containerSrc).slice(0, 40) : ''}`
									: undefined
							}
						}
					}
				})
				tasks.observe({ type: 'longtask', buffered: true })
			} catch {}
		}

		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'visible') visibleSince = performance.now()
		})
		// Page lifecycle: a frozen or bfcache-restored page did not block; it
		// was suspended. Chrome fires freeze/resume, Safari pageshow.
		for (const type of ['resume', 'pageshow'] as const) {
			window.addEventListener(type, () => {
				visibleSince = performance.now()
			})
		}
		document.addEventListener('resume', () => {
			visibleSince = performance.now()
		})

		const beat = () => {
			const now = performance.now()
			const drift = now - lastBeat - HEARTBEAT_MS
			const task = longest
			longest = null
			const blockedMs = task && task.duration >= FREEZE_MIN_MS ? task.duration : drift
			// A background tab throttles timers to once a minute; that is not a freeze.
			// Nor is a heartbeat-only gap longer than SUSPEND_MS: iOS suspends a
			// page on lock or app switch without always reporting it hidden, and
			// Safari has no Long Tasks API to prove a real block. A genuine
			// main-thread block that long gets the page killed by the OS.
			const heartbeatOnly = !(task && task.duration >= FREEZE_MIN_MS)
			const suspended = heartbeatOnly && drift >= SUSPEND_MS
			const trustworthy =
				document.visibilityState === 'visible' && lastBeat >= visibleSince && !suspended
			if (blockedMs >= FREEZE_MIN_MS && trustworthy && reports < MAX_REPORTS_PER_TAB) {
				reports += 1
				const nodesAfter = document.getElementsByTagName('*').length
				const memory = (performance as any).memory
				const info: FreezeInfo = {
					blockedMs: Math.round(blockedMs),
					nodesBefore,
					nodesAfter,
					addedTotal,
					removedTotal,
					added: Object.entries(added).sort((a, b) => b[1] - a[1]),
					recentRequests: recent
						.filter((r) => r.at <= now - blockedMs + 250)
						.slice(-3)
						.map((r) => r.path),
					source: task && task.duration >= FREEZE_MIN_MS ? 'longtask' : 'heartbeat',
					...(task?.attribution ? { attribution: task.attribution } : {}),
					...(commitHookInstalled ? { commits: commits - commitsAtBeat } : {}),
					...(memory && typeof memory.usedJSHeapSize === 'number'
						? { heapMb: Math.round(memory.usedJSHeapSize / 1e6) }
						: {})
				}
				try {
					report(info)
				} catch {}
			}
			lastBeat = now
			nodesBefore = document.getElementsByTagName('*').length
			addedTotal = 0
			removedTotal = 0
			added = {}
			commitsAtBeat = commits
			window.setTimeout(beat, HEARTBEAT_MS)
		}
		window.setTimeout(beat, HEARTBEAT_MS)
	} catch {}
}
